import { SubmissionErrors } from 'final-form';
import { PureComponent } from 'react';
import { Form } from 'react-final-form';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect, MapDispatchToPropsFunction } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import { getCompanyContentLocale } from '~src/services/session';
import {
  fetchTopicsForSurvey,
  FetchTopicsForSurveyParams,
  TopicsForSurvey,
} from '~src/modules/surveys/actions/topicActions';
import { Survey, SurveyScopes, NewSurveySubmitData } from '~src/modules/surveys/interfaces';
import ContainerHeader from '~src/modules/surveys/components/topic_select/headers/ContainerHeader';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import { StoreI } from '~src/rootReducer';

import ButtonSection from './ButtonSection';
import SurveyTopicAdd from './SurveyTopicAdd';
import { NestedCheckboxContainer } from './topicQuestionsForm.sc';
import TopicQuestionsFormInner from './TopicQuestionsFormInner';

export interface FormValuesI {
  questions: Record<string, Record<string, boolean>>;
  mandatory: Record<string, boolean>;
}

interface OwnProps {
  survey: Survey;
  surveyId: string;
  onFormSubmit: (request: TopicValueI) => Promise<SubmissionErrors | void>;
  changeQuestionCount: (count: number) => void;
  questionCount: number;
  isNewSurvey: boolean;
}

interface StateProps {
  initialValues: Record<string, any>;
  loadingQuestionsSave: boolean;
  defaultContentLocale: string;
}

interface DispatchProps {
  fetchTopicsForSurvey: (
    surveyId: string,
    params: FetchTopicsForSurveyParams,
  ) => ApiReduxResp<TopicsForSurvey>;
}

type Props = OwnProps & StateProps & DispatchProps & WithTranslation & RouteComponentProps<any>;

interface State {
  createdTopic: string | null;
  addTopicError: string | null;
  showSave: boolean;
  loadingInit: boolean;
}

export interface TopicValueI {
  surveyId: string;
  body: NewSurveySubmitData;
}

class TopicQuestionsForm extends PureComponent<Props, State> {
  isSurveyScopePrivate = this.props.survey.scope !== SurveyScopes.Admin;
  state: State = {
    createdTopic: null,
    addTopicError: null,
    showSave: false,
    loadingInit: true,
  };

  componentDidMount() {
    this.setState({ loadingInit: false });
  }

  mapQuestionValues = (values: FormValuesI): Promise<void> => {
    // we need to get the questionIds from the form
    const questionIds: string[] = [];
    Object.values(values.questions).forEach((val) => {
      Object.keys(val).forEach((keyId) => {
        if (val[keyId]) {
          questionIds.push(keyId);
        }
      });
    });
    let questionsIdSet = questionIds;
    if (this.props.survey.questions) {
      const oldQuestionIds = this.props.survey.questions.map(x => x.question_id);
      const newFilteredQuestionIds = oldQuestionIds.filter(x => questionIds.includes(x));
      const newQuestionIds = questionIds.filter(x => !oldQuestionIds.includes(x));
      questionsIdSet = newFilteredQuestionIds.concat(newQuestionIds);
    }
    const tmpValues: TopicValueI = {
      surveyId: this.props.survey.id,
      body: {
        name: this.props.survey.name,
        description: this.props.survey.description,
        questions: questionsIdSet.map((x) => ({ question_id: x, mandatory: !!values.mandatory[x] })),
        invitation: this.props.survey.invitation,
      },
    };

    return this.submitting(tmpValues, questionIds);
  };

  submitting = async (tmpValues: TopicValueI, questions: string[]) => {
    await this.props.onFormSubmit(tmpValues);
    this.props.changeQuestionCount(questions.length);
    this.setState({ showSave: false });
    this.props.history.replace(`/content/surveys/${this.props.survey.id}/summary`);
  };

  discardChanges = () => {
    if (this.props.isNewSurvey || this.props.questionCount < 1) {
      this.props.history.goBack();
    } else {
      this.props.history.replace(`/content/surveys/${this.props.surveyId}/summary`);
    }
  };

  changeShowSave = (value: boolean) => {
    this.setState({ showSave: value });
  };

  createTopic = (topicId: string) => {
    this.setState({ createdTopic: topicId });
  };

  render() {
    const { t, initialValues, defaultContentLocale } = this.props;
    const mandatoryQuestionIds: string[] = [];
    if (this.props.survey.questions) {
      this.props.survey.questions
        .filter((v) => v.mandatory)
        .forEach((v) => {
          mandatoryQuestionIds.push(v.question_id);
        });
    }

    if (initialValues.questions && Object.keys(initialValues.questions).length > 0) {
      return (
        <Form keepDirtyOnReinitialize initialValues={initialValues} onSubmit={this.mapQuestionValues}>
          {({ handleSubmit, form, values }) => {
            const deleteField = (field: string) => {
              form.change(field as any /* todo correct type */, undefined);
            };

            return (
              //TODO: Change isPrivate to isScopeAdmin
              <NestedCheckboxContainer $isPrivate={this.isSurveyScopePrivate} onSubmit={handleSubmit}>
                <ContainerHeader
                  t={t}
                  history={this.props.history}
                  isPrivate={this.isSurveyScopePrivate}
                  surveyId={this.props.surveyId}
                  isNewSurvey={this.props.isNewSurvey}
                  showSave={this.state.showSave}
                  selectedQuestions={values.questions || {}}
                />
                <TopicQuestionsFormInner
                  survey={this.props.survey}
                  isPrivate={this.isSurveyScopePrivate}
                  loadingInit={this.state.loadingInit}
                  selectedQuestions={values.questions || {}}
                  mandatoryQuestions={mandatoryQuestionIds}
                  changeShowSave={this.changeShowSave}
                  defaultContentLocale={defaultContentLocale}
                  createdTopic={this.state.createdTopic}
                  deleteField={deleteField}
                />
                {!this.isSurveyScopePrivate && (
                  <SurveyTopicAdd
                    defaultContentLocale={defaultContentLocale}
                    survey={this.props.survey}
                    t={t}
                    createTopic={this.createTopic}
                  />
                )}
                <ButtonSection
                  isPrivate={this.isSurveyScopePrivate}
                  showSave={this.state.showSave}
                  discardChanges={this.discardChanges}
                  t={t}
                  loadingSave={this.props.loadingQuestionsSave}
                />
              </NestedCheckboxContainer>
            );
          }}
        </Form>
      );
    } else {
      return null;
    }
  }
}

function mapStateToProps(state: StoreI): StateProps {
  const mandatory: Record<string, boolean> = {};
  if (state.surveyManagement.surveys.survey && state.surveyManagement.surveys.survey.questions) {
    state.surveyManagement.surveys.survey.questions
      .filter((q) => q.mandatory)
      .forEach((q) => {
        mandatory[q.question_id] = true;
      });
  }

  return {
    initialValues: { questions: state.topics.surveyTopics.topicSelectMap, mandatory },
    loadingQuestionsSave: state.surveyManagement.surveys.loadingQuestionsSave,
    defaultContentLocale: getCompanyContentLocale(state),
  };
}

const mapDispatchToProps: MapDispatchToPropsFunction<DispatchProps, unknown> = (dispatch) => ({
  fetchTopicsForSurvey: (surveyId, params) => dispatch(fetchTopicsForSurvey(surveyId, params)),
});

const TopicQuestionsFormR = withRouter(TopicQuestionsForm);
const TopicQuestionsFormT = withTranslation()(TopicQuestionsFormR);

const TopicQuestionsFormC = connect(mapStateToProps, mapDispatchToProps)(TopicQuestionsFormT);

export default TopicQuestionsFormC;
